import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

export function Notifications() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, mentions, system

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const loadNotifications = async () => {
      try {
        setLoading(true);
        const data = await apiService.getNotifications({ limit: 50 });
        setNotifications(data.notifications || []);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [isAuthenticated, navigate]);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'mentions') return n.type === 'mention';
    if (filter === 'system') return n.type === 'system';
    return true;
  });

  const getIcon = (type) => {
    switch (type) {
      case 'like': return '❤️';
      case 'comment': return '💬';
      case 'follow': return '👤';
      case 'mention': return '👋';
      case 'system': return '🔔';
      default: return 'bell';
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Notifications</h1>
        <div className="flex gap-2">
          {['all', 'mentions', 'system'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition ${
                filter === f 
                  ? 'bg-white text-slate-900' 
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading updates...</div>
      ) : filteredNotifications.length > 0 ? (
        <div className="space-y-2">
          {filteredNotifications.map((notification) => (
            <div 
              key={notification.id}
              className={`p-4 rounded-2xl border border-white/5 bg-white/5 hover:bg-white/10 transition flex gap-4 ${
                !notification.read ? 'border-l-4 border-l-blue-500' : ''
              }`}
            >
              <div className="text-2xl">{getIcon(notification.type)}</div>
              <div className="flex-1">
                <p className="text-white">
                  <span className="font-bold">{notification.actor?.name || 'Someone'}</span>
                  {' '}
                  {notification.message || 'interacted with your content'}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {new Date(notification.created_at).toLocaleDateString()}
                </p>
              </div>
              {notification.link && (
                <Link 
                  to={notification.link}
                  className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-bold self-center"
                >
                  View
                </Link>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-12 text-center">
          <div className="text-6xl mb-4">🔔</div>
          <p className="text-slate-400">No notifications yet</p>
          <p className="text-sm text-slate-500 mt-2">
            We'll notify you about activity on your account, community, and feed here.
          </p>
        </div>
      )}
    </div>
  );
}

export default Notifications;
